import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import { AutoSizer, Grid, Table, Column } from 'react-virtualized';

import BrLoader from '@components/BrLoader';
import CellRenderer from './CellRenderer';
import HeaderRenderer from './HeaderRenderer';

/* eslint-disable react/no-array-index-key */
export default class TableRenderer extends PureComponent {
  static propTypes = {
    columnMap: PropTypes.objectOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
      }),
    ).isRequired,
    displayColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
    displayData: PropTypes.arrayOf(PropTypes.object).isRequired,
    minWidth: PropTypes.number.isRequired,
    isRowSelectable: PropTypes.bool.isRequired,
    sortConfig: PropTypes.shape({ columnKey: PropTypes.string.isRequired, orderBy: PropTypes.string.isRequired })
      .isRequired,
    filterConfig: PropTypes.object.isRequired,
    selectedRows: PropTypes.arrayOf(PropTypes.object).isRequired,
    isLoading: PropTypes.bool.isRequired,
    isGrid: PropTypes.bool.isRequired,
    // this is used to set ref to grid to parent, so parent can perform force update on the grid upon data change
    onTableLoaded: PropTypes.func.isRequired,
    onSortClicked: PropTypes.func.isRequired,
    onSetFilter: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = { displayEdge: '' }; // may use initial props
  }

  componentDidMount() {
    this.props.onTableLoaded(this.grid);
  }

  getCheckboxRenderCell = (row) => ({ value: this.props.selectedRows.includes(row) });
  getRowHeight = ({ index }) => (index === 0 ? 40 : this.props.displayData[index - 1].height || 64);

  getColumnWidth = ({ index }) => {
    const { columnMap, displayColumns } = this.props;
    const col = columnMap[displayColumns[index]];
    return col.width || col.column.defaultWidth || 200;
  };

  handleTableScroll = ({ clientHeight, clientWidth = 0, scrollHeight, scrollWidth = 0, scrollTop, scrollLeft = 0 }) => {
    let displayEdge = 'br-table-edge-shadow';
    if (scrollLeft) {
      displayEdge += '_L';
    }
    if (scrollLeft + clientWidth < scrollWidth) {
      displayEdge += '_R';
    }
    if (scrollTop + clientHeight < scrollHeight) {
      displayEdge += '_B';
    }
    this.setState({ displayEdge });
  };

  renderGridCell = ({ columnIndex, rowIndex, key, style }) => {
    const isHeaderRow = rowIndex === 0;
    const className = isHeaderRow ? 'br-table-grid-header-cell' : 'br-table-grid-content-cell';
    const columnKey = this.props.displayColumns[columnIndex];
    return (
      <div className={className} key={key} style={style}>
        {isHeaderRow
          ? this.renderHeaderCell({ columnKey })
          : this.renderContentCell({ rowIndex: rowIndex - 1, columnIndex })}
      </div>
    );
  };

  renderHeaderCell = ({ columnKey }) => {
    const {
      columnMap,
      sortConfig: { columnKey: sortColumnKey, orderBy },
      filterConfig,
      onSortClicked,
      onSetFilter,
    } = this.props;
    const column = columnMap[columnKey] || {};
    const sortDirection = sortColumnKey === columnKey ? orderBy : '';
    return HeaderRenderer(column, sortDirection, filterConfig, onSortClicked, onSetFilter);
  };

  renderContentCell = ({ rowIndex, columnIndex }) => {
    const { displayData, displayColumns, columnMap, isRowSelectable } = this.props;
    const row = displayData[rowIndex] || {};
    const col = columnMap[displayColumns[columnIndex]] || {};
    const cell = row[col.key] || {};
    const cellRenderProps = { row, col, cell, columnIndex, rowIndex };
    // fills in col info for checkbox column
    // eslint-disable-next-line eqeqeq
    if (isRowSelectable && columnIndex == 0) {
      cellRenderProps.cell = this.getCheckboxRenderCell(row);
    }
    return CellRenderer(cellRenderProps);
  };

  render() {
    const { columnMap, displayColumns, displayData, minWidth, isLoading, isGrid } = this.props;
    return (
      <div className={`br-table-table-renderer ${this.state.displayEdge}`}>
        {isLoading && (
          <div className="is-loading-icon">
            <BrLoader />
          </div>
        )}
        <AutoSizer>
          {({ height, width }) => (isGrid ? (
            <Grid
              ref={(grid) => { this.grid = grid; }}
              height={height}
              width={Math.max(minWidth, width)}
              rowCount={displayData.length + 1}
              columnCount={displayColumns.length}
              rowHeight={this.getRowHeight}
              columnWidth={this.getColumnWidth}
              cellRenderer={this.renderGridCell}
              onScroll={this.handleTableScroll}
            />
          ) : (
            <Table
              ref={(grid) => { this.grid = grid; }}
              headerHeight={40}
              height={height}
              rowHeight={({ index }) => this.getRowHeight({ index: index + 1 })}
              rowGetter={({ index }) => displayData[index]}
              rowCount={displayData.length}
              width={Math.max(minWidth, width)}
              onScroll={this.handleTableScroll}
            >
              {displayColumns.map((columnKey, index) => {
                const column = columnMap[columnKey];
                return (
                  <Column
                    key={index}
                    dataKey={columnKey}
                    cellRenderer={this.renderContentCell}
                    headerRenderer={() => this.renderHeaderCell({ columnKey })}
                    flexGrow={column.flexGrow || 0}
                    flexShrink={column.flexShrink || 0}
                    label={column.displayName || column.key}
                    width={column.width || (column.column && column.column.defaultWidth) || 200}
                  />
                );
              })}
            </Table>
          ))}
        </AutoSizer>
      </div>
    );
  }
}
